import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import connect from '@app/utils/connect';
import * as R from 'ramda';
import ZIP from 'jszip';
import * as Sentry from '@sentry/react';

import broadcastActions from '@app/actions/broadcasts';
import { Notification } from '@app/components/notifications/Notifications';
import usePrevious from '@app/components/hooks/usePrevious';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import {
    BROADCAST_DEMOGRAPHICS,
    BROADCAST_IMPRESSIONS,
    EDGE_RIGHTSHOLDER_ATTRIBUTION,
    FILTER_REDESIGN,
} from '@app/constants/config';
import { getBroadcastsQuery } from '@app/actions/broadcasts/setBroadcasts';
import { Client } from '@app/types/api';
import RouteNames from '@app/RouteNames';
import {
    BroadcastTab,
    getFiltersToSave,
    getFiltersWithClearedDemographics,
    getVerbalMentionPlacementIds,
    hasSelectedDemographicFilters,
} from './utils/broadcastsUtils';
import { DemographicFilters } from '@app/constants/demographics';
import {
    fetchFrameBlob,
    getFrameFileName,
    getFrameFolderName,
    saveBlob,
} from './utils/frameDownloadUtils';
import useBroadcasts from './hooks/useBroadcasts';
import { useRightsholderAttribution } from '@app/components/hooks/useRightsholderAttribution';

interface BroadcastsContextProps {
    setBroadcasts: (
        fetchedBroadcasts: any,
        isLoadingBroadcasts: boolean,
        query: any,
    ) => void;
    getBroadcastNames: () => void;
    broadcasts: any;
    filters: any;
    saveFilters: (newFilters: any) => void;
    clearOverrideFilters: () => void;
    setIsReturningFromOverride: () => void;
    sendNotification: (
        message: string,
        config?: Notification['options'],
    ) => void;
    client: Client;
    children: React.ReactNode;
}

interface BroadcastsContextValue {
    setBroadcasts: (
        fetchedBroadcasts: any,
        isLoadingBroadcasts: boolean,
        query: any,
    ) => void;
    getBroadcastNames: () => void;
    broadcasts: any;
    filters: {
        changesId?: string;
        overridesId?: string;
        placements?: any;
        isLoading?: boolean;
        sponsors: {
            list: any;
            selected: any;
        };
        rightsholders: {
            list: any;
            selected: any;
        };
    };
    clearOverrideFilters: () => void;
    setIsReturningFromOverride: () => void;
    client: Client;
    query: {
        page: number;
        pageSize: number;
        mediaUploadNames: string[];
    };
    activeTab: BroadcastTab;
    demographicFilters: DemographicFilters;
    navigate: ReturnType<typeof useNavigate>;
    selectedNames: Record<string, boolean>;
    viewershipType: 'individual' | 'household';
    isDownloadingFrames: boolean;
    viewershipFilteredBroadcastName: string;
    shouldDisplayPageFilters: boolean;
    showExistingPosts: boolean;
    nameOptions: any;
    hasPostsAvailable: boolean;
    _scrollBottom: () => void;
    _scrollTop: () => void;
    _fetchFirstPage: () => void;
    broadcastsQuery: any;
    _nextPage: () => void;
    downloadFrames: () => void;
    title: string;
    insightsTitle: string;
    hasBroadcastImpressionsPermission: boolean;
    hasBroadcastDemographicsPermission: boolean;
    onGoBackToInsights: () => void;
    toggleViewershipType: () => void;
    isLoading: boolean;
    order: any;
    morePages: boolean;
    filtersOverride: any;
    setActiveTab: (tab: BroadcastTab) => void;
    selectNames: (names: Record<string, boolean>) => void;
    setViewershipFilteredBroadcastName: (name: string) => void;
    isAttributionFiltersReady: boolean;
}

export const BroadcastsContext =
    React.createContext<BroadcastsContextValue | null>(null);

const BroadcastsProvider = ({
    setBroadcasts,
    getBroadcastNames,
    broadcasts,
    filters,
    saveFilters,
    clearOverrideFilters,
    setIsReturningFromOverride,
    sendNotification,
    client,
    children,
}: BroadcastsContextProps) => {
    const { t } = useTranslation('measurement');
    const { rightsholderAttribution } = useRightsholderAttribution({ client });
    const [query, setQuery] = useState({
        page: 0,
        pageSize: 5,
        mediaUploadNames: [],
        mediaUploadNamesOverride: [],
    });
    const navigate = useNavigate();
    const { activeTab: currentTab } = useParams();
    const { permissions = [] } = client ?? {};
    const hasRightsholderAttributionPermission = permissions.includes(
        EDGE_RIGHTSHOLDER_ATTRIBUTION,
    );
    const expectedAttributionMode =
        hasRightsholderAttributionPermission &&
        rightsholderAttribution === 'league'
            ? 'league'
            : 'team';
    const isAttributionFiltersReady =
        !filters?.isLoading &&
        (!hasRightsholderAttributionPermission ||
            filters?.rightsholderAttributionMode === expectedAttributionMode);
    const hasBroadcastDemographicsPermission = permissions.includes(
        BROADCAST_DEMOGRAPHICS,
    );
    // The Demographics tab is not accessible (even by direct URL) without
    // its permission; fall back to the default Sample Exposures tab.
    const isTabAccessible =
        currentTab !== BroadcastTab.DEMOGRAPHICS ||
        hasBroadcastDemographicsPermission;
    const [activeTab, setActiveTab] = useState<BroadcastTab>(
        isTabAccessible && currentTab
            ? (currentTab as BroadcastTab)
            : BroadcastTab.EXPOSURES,
    );
    const [tabPlacements, setTabPlacements] = useState({
        ...filters.placements,
        selected: [],
    });
    const [tabPlacementTypes, setTabPlacementTypes] = useState({
        ...filters.placementTypes,
        selected: [],
    });
    const [selectedNames, selectNames] = useState<Record<string, boolean>>({});
    const [viewershipType, setViewershipType] = useState<
        'individual' | 'household'
    >('individual');
    const previousFilters: {
        changesId?: string;
        overridesId?: string;
    } = usePrevious(filters);
    const [isDownloadingFrames, setDownloadingFrames] = useState(false);
    const [
        viewershipFilteredBroadcastName,
        setViewershipFilteredBroadcastName,
    ] = useState('');
    const shouldDisplayPageFilters = permissions.includes(FILTER_REDESIGN);
    const {
        isLoading,
        order,
        hasMore: morePages,
        names,
        filtersOverride,
    } = broadcasts;

    const broadcastsQuery = useMemo(
        () => getBroadcastsQuery(filters, client, broadcasts, query),
        [filters, client, broadcasts, query],
    );

    const { data: fetchedBroadcasts, isLoading: isLoadingBroadcasts } =
        useBroadcasts({
            query: R.omit(
                ['mediaUploadNames', 'mediaUploadNamesOverride'],
                query,
            ),
            filters: broadcastsQuery,
            enabled: isAttributionFiltersReady,
        });

    useEffect(() => {
        if (!isLoadingBroadcasts) {
            setBroadcasts(fetchedBroadcasts, isLoadingBroadcasts, query);
        }
    }, [fetchedBroadcasts, isLoadingBroadcasts, query]);

    const showExistingPosts = !isLoading || query.page > 0;

    useEffect(() => {
        if (currentTab !== activeTab) {
            // The previous tab was the VM tab
            if (currentTab === BroadcastTab.VERBAL_MENTIONS) {
                const newFilters = getFiltersToSave({
                    ...filters,
                    placements: {
                        ...tabPlacements,
                    },
                    placementTypes: {
                        ...tabPlacementTypes,
                    },
                });
                saveFilters(newFilters);
            }
            // Demographic filters are scoped to the Demographics tab: drop
            // them on the way out, keep every other filter as is.
            if (
                currentTab === BroadcastTab.DEMOGRAPHICS &&
                hasSelectedDemographicFilters(filters)
            ) {
                saveFilters(getFiltersWithClearedDemographics(filters));
            }
            const path = generatePath(
                `${RouteNames.broadcasts.path}/${RouteNames.broadcastsTabs.path}`,
                {
                    clientId: String(client.id),
                    activeTab: activeTab,
                },
            );

            navigate(path);
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== BroadcastTab.VERBAL_MENTIONS) {
            setTabPlacements({ ...filters.placements });
            setTabPlacementTypes({ ...filters.placementTypes });
        }
    }, [activeTab]);

    useEffect(() => {
        if (
            activeTab === BroadcastTab.VERBAL_MENTIONS &&
            isAttributionFiltersReady
        ) {
            const vmPlacementIds = getVerbalMentionPlacementIds(filters);

            if (vmPlacementIds.length > 0) {
                const newFilters = getFiltersToSave({
                    ...filters,
                    placements: {
                        ...filters.placements,
                        selected: vmPlacementIds,
                    },
                    // Making sure that no placement types are applied when the user is in the VM tab
                    placementTypes: {
                        ...filters.placementTypes,
                        selected: [],
                    },
                });
                saveFilters(newFilters);
            }
        }
    }, [activeTab, isAttributionFiltersReady]);

    useEffect(() => {
        if (!isAttributionFiltersReady) return;
        (async () => {
            await Promise.all([getBroadcastNames(), _fetchFirstPage()]);
        })();
        return () => {
            if (filtersOverride) clearOverrideFilters();
        };
    }, [filters, rightsholderAttribution, isAttributionFiltersReady]);

    useEffect(() => {
        setViewershipFilteredBroadcastName('');
    }, [filters]);

    useEffect(() => {
        (async () => {
            // if not loading and filters have changed or broadcasts have changed
            if (
                isAttributionFiltersReady &&
                !isLoading &&
                ((previousFilters &&
                    previousFilters.changesId &&
                    filters.changesId &&
                    filters.changesId !== previousFilters.changesId) ||
                    (previousFilters &&
                        previousFilters.overridesId &&
                        filters.overridesId &&
                        filters.overridesId !== previousFilters.overridesId) ||
                    (broadcasts.changesId &&
                        filters.changesId &&
                        broadcasts.changesId !== filters.changesId))
            ) {
                // Fetch new data by getting broadcast names and fetching the first page
                await Promise.all([getBroadcastNames(), _fetchFirstPage()]);
                setQuery({ ...query, page: 0 });
            }
        })();
    }, [
        previousFilters,
        filters,
        broadcasts,
        isLoading,
        rightsholderAttribution,
        isAttributionFiltersReady,
    ]);

    const nameOptions = useMemo(
        () =>
            (Array.isArray(names) ? names : []).map((name, i) => ({
                value: i,
                label: name,
            })),
        [names],
    );

    useEffect(() => {
        const mediaUploadNames = nameOptions.reduce((acc, { value, label }) => {
            if (selectedNames[value]) {
                acc.push(label);
            }
            return acc;
        }, []);
        setQuery({ ...query, mediaUploadNames });
    }, [selectedNames]);

    // Detailed viewbroadcast

    const hasPostsAvailable = order.length > 0;

    // Navigation with keyboard logic
    const _scrollBottom = () => {
        document.getElementById('bottom').scrollIntoView();
    };

    const _scrollTop = () => {
        document.getElementById('title').scrollIntoView();
    };

    const _fetchFirstPage = (filterBroadcastName?: string) => {
        let q = { ...query, page: 0, mediaUploadNamesOverride: undefined };
        if (filterBroadcastName) {
            q = { ...q, mediaUploadNamesOverride: [filterBroadcastName] };
        }
        setQuery(q);
    };

    const _nextPage = () => {
        const newQuery = { ...query, page: query.page + 1 };
        setQuery(newQuery);
    };

    const downloadFrames = useCallback(async () => {
        setDownloadingFrames(true);
        let downloadedCount = 0;
        let failedCount = 0;

        try {
            const zip = new ZIP();
            const { byId = {} } = broadcasts ?? {};

            await Promise.all(
                Object.values(byId).map(
                    async ({
                        broadcastRsps = [],
                        broadcastName = '',
                        contentDate,
                    }: any) => {
                        if (broadcastRsps.length === 0) return;
                        const folder = zip.folder(
                            getFrameFolderName(broadcastName, contentDate),
                        );
                        await Promise.all(
                            broadcastRsps.map(async (rsp: any) => {
                                // A failed frame is skipped so the rest of the
                                // zip can still be produced.
                                const blob = await fetchFrameBlob(
                                    rsp?.frameUrl,
                                );
                                if (!blob) {
                                    failedCount += 1;
                                    return;
                                }
                                folder?.file(getFrameFileName(rsp), blob);
                                downloadedCount += 1;
                            }),
                        );
                    },
                ),
            );

            if (downloadedCount === 0) {
                sendNotification(t('broadcastExposure.downloadFramesFailed'), {
                    context: 'error',
                });
                return;
            }

            const content = await zip.generateAsync({ type: 'blob' });
            saveBlob(content, `broadcast_frames_${client?.name}.zip`);

            if (failedCount > 0) {
                sendNotification(
                    t('broadcastExposure.downloadFramesPartial', {
                        count: failedCount,
                    }),
                    { context: 'warning' },
                );
            }
        } catch (error) {
            Sentry.withScope(scope => {
                scope.setTag('context', 'broadcast-frames-download');
                scope.setLevel('error');
                Sentry.captureException(error);
            });
            sendNotification(t('broadcastExposure.downloadFramesFailed'), {
                context: 'error',
            });
        } finally {
            // Always clear the loading state, even when every fetch rejects.
            setDownloadingFrames(false);
        }
    }, [broadcasts, client?.name, sendNotification, t]);

    const title = t('broadcastExposure.title');
    const insightsTitle = t('dataExplorer.title');

    const hasBroadcastImpressionsPermission = permissions.includes(
        BROADCAST_IMPRESSIONS,
    );

    const onGoBackToInsights = () => {
        setIsReturningFromOverride();
        //Navigate back from the override filters to the insights page
        navigate(`/c/${String(client.id)}/insights`);
    };

    const demographicFilters = useMemo<DemographicFilters>(
        () => ({
            ageBrackets: filters.ageBrackets ?? [],
            genders: filters.genders ?? [],
        }),
        [filters.ageBrackets, filters.genders],
    );

    const toggleViewershipType = () => {
        setViewershipType(prev =>
            prev === 'individual' ? 'household' : 'individual',
        );
    };

    const providerValue = {
        setBroadcasts,
        getBroadcastNames,
        broadcasts,
        filters,
        filtersOverride,
        clearOverrideFilters,
        setIsReturningFromOverride,
        client,
        query,
        activeTab,
        demographicFilters,
        navigate,
        selectedNames,
        viewershipType,
        isDownloadingFrames,
        viewershipFilteredBroadcastName,
        shouldDisplayPageFilters,
        showExistingPosts,
        nameOptions,
        hasPostsAvailable,
        _scrollBottom,
        _scrollTop,
        _fetchFirstPage,
        broadcastsQuery,
        _nextPage,
        downloadFrames,
        title,
        insightsTitle,
        hasBroadcastImpressionsPermission,
        hasBroadcastDemographicsPermission,
        onGoBackToInsights,
        toggleViewershipType,
        isLoading,
        setActiveTab,
        selectNames,
        setViewershipFilteredBroadcastName,
        order,
        morePages,
        isAttributionFiltersReady,
    };

    return (
        <BroadcastsContext.Provider value={providerValue}>
            {children}
        </BroadcastsContext.Provider>
    );
};

const ConnectedBroadcastsProvider = connect(
    ['broadcasts', 'filters', 'client'],
    broadcastActions,
)(BroadcastsProvider);

export const useBroadcastsContext = () => {
    const context = React.useContext(BroadcastsContext);
    if (!context) {
        throw new Error(
            'useBroadcastsContext must be used within a BroadcastsProvider',
        );
    }
    return context;
};

export default ConnectedBroadcastsProvider;
